import React from 'react';
import './CallInfoDrawer.styl';
import {CallToAction} from 'components/pure/tooltips/CallToAction';
import FieldContainer from 'components/pure/utils/form/FieldContainer';
import InputField from 'components/pure/utils/form/InputField';
import LiveCoachingSource from 'sources/scorecards/LiveCoachingSource';
import AsyncAutocomplete from 'components/pure/scorecards/live-coaching/autocomplete/AsyncAutocomplete';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import LiveCoachingModals from 'components/pure/scorecards/live-coaching/LiveCoachingModals';
import AuthStore from 'stores/AuthStore';
import TimeKeeper from 'react-timekeeper';
import DatePicker from 'components/pure/utils/form/DatePicker';
import moment from 'moment';
import ReviewTypeDropdown from 'components/dashboard/common/ReviewTypeDropdown';
import SelectizeLookingDropdownArrow from 'components/utils/SelectizeLookingDropdownArrow';
import CoachingReviewType, {CoachingReviewTypeId} from 'records/CoachingReviewType';
import LiveCoachingCall, {FileDirection, LiveCoachingCallId} from 'records/LiveCoachingCall';
import {IdMap} from '@store/@types/util';
import User from 'records/User';
import CoachingReview from 'records/CoachingReview';
import CoachingPlan from 'records/CoachingPlan';
import Scorecard from 'records/Scorecard';
import {List, Map} from 'immutable';
import Button from '@material-ui/core/Button';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';

// TODO Move to live coaching store or @types/livecoaching
interface DirectionOption {
  display: FileDirection;
  id: string; // String of an int
  type: 'idfield' | 'booleanfield' | 'stringfield';
}

interface CallInfoDrawerProps {
  users: IdMap<User>;
  directions: List<DirectionOption>;
  isEditingScorecard: boolean;
  hasSubmittedScorecards: boolean;
  onFinish: () => void;
  review: CoachingReview;
  plan: CoachingPlan;
  scorecards: List<Scorecard>;
  modelId: LiveCoachingCallId;
  taggedUsers?: unknown;
  sessionUser: User;
  answers?: unknown;
  nonAdHoc?: boolean;
  types: Map<CoachingReviewTypeId, CoachingReviewType>;
  allScored: boolean;
  model: LiveCoachingCall;
}

interface CallInfoDrawerState {
  model: LiveCoachingCall;
  modelEdited: boolean;
  finalizing: boolean;
  shouldShowTimeSelector: boolean;
  constrainingAccount?: string;
  constrainingContact?: string;
  reviewTypeId?: CoachingReviewTypeId;
}

export default class CallInfoDrawer extends React.Component<
  CallInfoDrawerProps,
  CallInfoDrawerState
> {
  constructor(props) {
    super(props);

    this.state = {
      model: props.model,
      modelEdited: false,
      finalizing: false,
      shouldShowTimeSelector: false,
      constrainingAccount: undefined,
      constrainingContact: undefined,
      reviewTypeId: props.plan.reviewTypeIds.first(),
    };

    this.setPhone = this.setPhone.bind(this);
    this.setExtension = this.setExtension.bind(this);
    this.setTime = this.setTime.bind(this);
    this.setDate = this.setDate.bind(this);
    this.setAccount = this.setAccount.bind(this);
    this.setContact = this.setContact.bind(this);
    this.setOpportunity = this.setOpportunity.bind(this);
    this.searchContacts = this.searchContacts.bind(this);
    this.searchOpportunities = this.searchOpportunities.bind(this);
    this.valid = this.valid.bind(this);
    this.finalize = this.finalize.bind(this);
  }

  componentDidMount() {
    this.setState({
      model: this.props.model,
    });
  }

  setPhone(phone, countryData) {
    const {dialCode} = countryData;
    const phoneWithoutCountry = phone.substring(dialCode.length);

    this.setState({
      modelEdited: true,
      model: this.state.model
        .set('phoneNumber', phoneWithoutCountry)
        .set('phoneCountryCode', dialCode),
    });
  }
  setExtension(ext) {
    this.setState({
      model: this.state.model.set('phoneExtension', ext),
      modelEdited: true,
    });
  }

  setTime(time) {
    const hour = time.hour24;
    const minute = time.minute;

    const newMoment = this.state.model.dateTime.clone().set('hour', hour).set('minute', minute);

    this.setState({
      model: this.state.model.set('dateTime', newMoment),
      modelEdited: true,
    });
  }

  setDate(date) {
    const newMoment = date
      .clone()
      .set('hour', this.state.model.dateTime.hour())
      .set('minute', this.state.model.dateTime.minute());

    this.setState({
      model: this.state.model.set('dateTime', newMoment),
      modelEdited: true,
    });
  }

  getTimeFormatted(time) {
    return time.format('h:mm a');
  }

  /**
   * Given an account field/object back, set the proper id on the model,
   * as well as save the exact response to fill in to properly constrain
   * on that account afterwards (for contact, opportunity)
   *
   * @param account
   */
  setAccount({id: account, freeformText}) {
    this.setState({
      modelEdited: true,
      model: this.state.model.set(
        'crmAccount',
        !account && !freeformText
          ? undefined
          : {
              id: account ? account.id : undefined,
              name: freeformText,
            }
      ),
      constrainingAccount: account,
    });
  }

  setContact({id: contact, freeformText}) {
    this.setState({
      model: this.state.model.set(
        'crmContact',
        !contact && !freeformText
          ? undefined
          : {id: contact ? contact.id : undefined, name: freeformText}
      ),
      constrainingContact: contact,
      modelEdited: true,
    });
  }

  setOpportunity({id: opportunity, freeformText}) {
    this.setState({
      modelEdited: true,
      model: this.state.model.set(
        'crmOpportunity',
        !opportunity && !freeformText
          ? undefined
          : {id: opportunity ? opportunity.id : undefined, name: freeformText}
      ),
    });
  }

  setDirection = (direction?: string) => {
    this.setState({
      model: this.state.model.set(
        'direction',
        this.props.directions.find(d => d.id === direction)?.display
      ),
    });
  };

  searchContacts(prefix) {
    const {constrainingAccount} = this.state;

    const choices = constrainingAccount
      ? {
          ACCOUNT: constrainingAccount,
        }
      : {};

    return LiveCoachingSource.searchContacts(prefix, choices);
  }

  searchOpportunities(prefix) {
    const {constrainingAccount} = this.state;

    const choices = constrainingAccount
      ? {
          ACCOUNT: constrainingAccount,
        }
      : {};

    return LiveCoachingSource.searchOpportunities(prefix, choices);
  }

  valid() {
    if (!this.state.model) return true;

    return !!this.state.model.dateTime && !this.state.shouldShowTimeSelector;
  }

  finalize() {
    this.setState({finalizing: true});
  }

  render() {
    const {
      users,
      directions,
      isEditingScorecard,
      hasSubmittedScorecards,
      onFinish,
      review,
      plan,
      scorecards,
      modelId,
      taggedUsers,
      sessionUser,
      answers,
      nonAdHoc,
      types,
      allScored,
    } = this.props;
    const {modelEdited, model, finalizing, shouldShowTimeSelector} = this.state;
    const loading = !users || !model;
    const skipConfirm = !loading && allScored && modelEdited;

    const rep = users.get(model.callerId);
    const coach = AuthStore.getState().user;

    const directionOptions = directions
      .map(d => ({
        value: d.id,
        label: d.display,
      }))
      .toArray();

    return (
      <div className="call-info-drawer">
        <div className="info-header">
          <h3>Call info</h3>
        </div>

        <div className="info-body">
          <div>
            {loading ? null : (
              <div className="form">
                <FieldContainer labelType="div" label="Rep">
                  <div className="input-wrapper">
                    <div className="rep-display">{rep ? rep.getFullName() : ''}</div>
                  </div>
                </FieldContainer>
                <FieldContainer labelType="div" label="Phone Number">
                  <div className="input-wrapper">
                    <div className="phone-input-wrapper">
                      <PhoneInput
                        country={'us'}
                        onChange={this.setPhone}
                        placeholder={'+1 (111) 867-5309'}
                      />
                    </div>
                  </div>
                </FieldContainer>
                <FieldContainer labelType="div" label="Phone Extension">
                  <div className="input-wrapper">
                    <div className="ext-wrapper">
                      <InputField
                        placeholder={'Ext'}
                        onChange={this.setExtension}
                        onKeyPress={event => {
                          const ext = event.currentTarget.value;
                          if (Number(ext)) {
                            this.setExtension(ext);
                          }
                        }}
                      />
                    </div>
                  </div>
                </FieldContainer>
                <FieldContainer labelType="div" label="Call Time">
                  <div className="input-wrapper">
                    <div className="datetime-wrapper">
                      {shouldShowTimeSelector ? (
                        <TimeKeeper
                          closeOnMinuteSelect
                          switchToMinuteOnHourSelect
                          onDoneClick={t => {
                            this.setTime(t);
                            this.setState({shouldShowTimeSelector: false});
                          }}
                        />
                      ) : (
                        <div className={'display-wrapper'}>
                          <div className={'date-picker'}>
                            <DatePicker
                              value={model.dateTime}
                              maxDate={moment()}
                              minDate={moment().subtract(10, 'days')}
                              onChange={this.setDate}
                            />
                          </div>
                          <div
                            className={'time-display'}
                            onClick={() => this.setState({shouldShowTimeSelector: true})}>
                            <span>{this.getTimeFormatted(model.dateTime)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </FieldContainer>
                <FieldContainer labelType="div" label="Account Name">
                  <div className="input-wrapper">
                    <AsyncAutocomplete
                      doSearch={LiveCoachingSource.searchAccounts}
                      onSelect={this.setAccount}
                      placeholder={'Acme Corp'}
                      width={210}
                      allowFreeform
                    />
                  </div>
                </FieldContainer>
                <FieldContainer labelType="div" label="Contact/Lead Name">
                  <div className="input-wrapper">
                    <AsyncAutocomplete
                      doSearch={this.searchContacts}
                      onSelect={this.setContact}
                      placeholder={'John Smith'}
                      width={210}
                      allowFreeform
                    />
                  </div>
                </FieldContainer>
                <FieldContainer labelType="div" label="Opportunity">
                  <div className="input-wrapper">
                    <AsyncAutocomplete
                      doSearch={this.searchOpportunities}
                      onSelect={this.setOpportunity}
                      placeholder={'Opportunity Name'}
                      width={210}
                      allowFreeform
                    />
                  </div>
                </FieldContainer>
                {directions && !directions.isEmpty() && (
                  <FieldContainer labelType="div" label="Call Direction">
                    <div className="input-wrapper">
                      <div className="direction-selector-wrapper">
                        <Autocomplete
                          className="direction-dropdown"
                          renderInput={params => (
                            <TextField
                              {...params}
                              size="small"
                              variant="outlined"
                              className="direction-dropdown-input"
                            />
                          )}
                          getOptionLabel={opt => opt.label}
                          getOptionSelected={(opt, selected) => opt.value === selected.value}
                          value={directionOptions.find(d => d.value === model.direction)}
                          options={directionOptions}
                          onChange={(e, opt) => this.setDirection(opt?.value)}
                        />
                      </div>
                    </div>
                  </FieldContainer>
                )}
                {plan.livecoaching && (
                  <FieldContainer labelType="div" label="Review Type">
                    <div className="input-wrapper">
                      <div className="review-type-selector-wrapper">
                        <ReviewTypeDropdown
                          width={206}
                          value={this.state.reviewTypeId}
                          coachingReviewTypes={types.filter(t => t.includesCalls).toList()}
                          onSelect={({id}) => this.setState({reviewTypeId: id as number})}
                          arrow={(open, onShowDropdown) => (
                            <SelectizeLookingDropdownArrow
                              open={open}
                              onShowDropdown={onShowDropdown}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </FieldContainer>
                )}
              </div>
            )}
          </div>
          <div>
            <CallToAction
              text={
                'Enter as much information about the call as you know.  When you are done coaching hit the Submit button below.'
              }
            />
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={() => (this.valid() ? this.finalize() : null)}>
              Submit
            </Button>
          </div>
        </div>
        <LiveCoachingModals
          reviewTypeId={this.state.reviewTypeId}
          plan={plan}
          scorecards={scorecards}
          nonAdHoc={nonAdHoc}
          modelId={modelId}
          taggedUsers={taggedUsers}
          sessionUser={sessionUser}
          answers={answers}
          call={model}
          rep={rep}
          coach={coach}
          review={review}
          isEditingScorecard={isEditingScorecard}
          hasSubmittedScorecards={hasSubmittedScorecards}
          finalizing={finalizing}
          onFinish={onFinish}
          onCancelSubmit={() => this.setState({finalizing: false})}
          adminId={coach.isAdmin() ? coach.id : undefined}
          managerId={coach.isAdmin() ? undefined : coach.id}
          skipConfirm={skipConfirm}
        />
      </div>
    );
  }
}
